import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Eye,
    Users,
    Repeat,
    FileText,
    RefreshCcw,
    Monitor,
    Globe,
    MapPin,
} from "lucide-react";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from "recharts";
import { useAuth } from "../../../context/AuthContext";
import { fetchAnalytics } from "../../../services/api";
import styles from "./PainelAnalytics.module.css";

const RANGES = [
    { value: "7d", label: "7 dias" },
    { value: "30d", label: "30 dias" },
    { value: "90d", label: "90 dias" },
];

const PIE_COLORS = ["#1a73e8", "#00c4b4", "#f59e0b", "#a855f7", "#ef4444", "#64748b"];

function formatNumber(value) {
    return Number(value || 0).toLocaleString("pt-BR");
}

function formatDateLabel(value) {
    // value = YYYY-MM-DD
    const parts = (value || "").split("-");
    if (parts.length !== 3) return value;
    return `${parts[2]}/${parts[1]}`;
}

const STAT_CARDS = [
    { key: "total_views", label: "Acessos totais", icon: Eye, accent: "#1a73e8" },
    { key: "unique_visitors", label: "Visitantes únicos", icon: Users, accent: "#00c4b4" },
    { key: "returning_views", label: "Recorrentes", icon: Repeat, accent: "#a855f7" },
    { key: "posts_lidos", label: "Posts lidos", icon: FileText, accent: "#f59e0b" },
];

export default function PainelAnalytics() {
    const { password } = useAuth();
    const [range, setRange] = useState("30d");
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        try {
            setIsLoading(true);
            setError("");
            const res = await fetchAnalytics(password, range);
            setData(res);
        } catch (err) {
            setError(err?.message || "Não foi possível carregar os dados de acesso.");
        } finally {
            setIsLoading(false);
        }
    }, [password, range]);

    useEffect(() => {
        load();
    }, [load]);

    const totals = data?.totals || {};
    const series = data?.series || [];
    const topPosts = data?.top_posts || [];
    const devices = data?.devices || [];
    const browsers = data?.browsers || [];
    const sources = data?.sources || [];
    const locations = data?.locations || [];

    const hasAnyData = useMemo(
        () => (totals.total_views || 0) > 0,
        [totals.total_views]
    );

    return (
        <div className={styles.page}>
            <header className={styles.topbar}>
                <div>
                    <span className={styles.eyebrow}>Painel administrativo</span>
                    <h1>Acessos</h1>
                    <p>Acompanhe o tráfego do site e quais conteúdos mais engajam.</p>
                </div>

                <div className={styles.topbarActions}>
                    <div className={styles.rangeTabs}>
                        {RANGES.map((r) => (
                            <button
                                key={r.value}
                                type="button"
                                className={`${styles.rangeTab} ${
                                    range === r.value ? styles.rangeTabActive : ""
                                }`}
                                onClick={() => setRange(r.value)}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>

                    <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={load}
                        disabled={isLoading}
                    >
                        <RefreshCcw size={17} aria-hidden="true" />
                        Atualizar
                    </button>
                </div>
            </header>

            {error && (
                <div className={styles.errorMessage}>
                    <strong>Ops!</strong>
                    <span>{error}</span>
                </div>
            )}

            {/* Cards de resumo */}
            <div className={styles.statsGrid}>
                {STAT_CARDS.map(({ key, label, icon: Icon, accent }) => (
                    <div key={key} className={styles.statCard}>
                        <div
                            className={styles.statIcon}
                            style={{ background: `${accent}1a`, color: accent }}
                        >
                            <Icon size={20} />
                        </div>
                        <div className={styles.statBody}>
                            <span>{label}</span>
                            <strong>{formatNumber(totals[key])}</strong>
                        </div>
                    </div>
                ))}
            </div>

            {isLoading && !data ? (
                <div className={styles.loading}>Carregando dados de acesso...</div>
            ) : !hasAnyData ? (
                <div className={styles.empty}>
                    <Eye size={28} aria-hidden="true" />
                    <h2>Ainda não há acessos registrados</h2>
                    <p>
                        Assim que os visitantes navegarem pelo site, os números
                        aparecerão aqui automaticamente.
                    </p>
                </div>
            ) : (
                <>
                    {/* Série de acessos no tempo */}
                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h3>Acessos ao longo do tempo</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={series} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#1a73e8" stopOpacity={0.35} />
                                        <stop offset="95%" stopColor="#1a73e8" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#00c4b4" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#00c4b4" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={formatDateLabel}
                                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                                    tickLine={false}
                                    axisLine={false}
                                    minTickGap={24}
                                />
                                <YAxis
                                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                                    tickLine={false}
                                    axisLine={false}
                                    allowDecimals={false}
                                    width={36}
                                />
                                <Tooltip
                                    labelFormatter={(l) => `Dia ${formatDateLabel(l)}`}
                                    formatter={(value, name) => [
                                        formatNumber(value),
                                        name === "views" ? "Acessos" : "Visitantes",
                                    ]}
                                    contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13 }}
                                />
                                <Area type="monotone" dataKey="views" stroke="#1a73e8" strokeWidth={2} fill="url(#gViews)" />
                                <Area type="monotone" dataKey="visitors" stroke="#00c4b4" strokeWidth={2} fill="url(#gVisitors)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </section>

                    <div className={styles.grid2}>
                        {/* Top posts */}
                        <section className={styles.card}>
                            <div className={styles.cardHeader}>
                                <FileText size={17} aria-hidden="true" />
                                <h3>Posts mais lidos</h3>
                            </div>
                            {topPosts.length === 0 ? (
                                <p className={styles.muted}>Nenhum post acessado no período.</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={Math.max(180, topPosts.length * 42)}>
                                    <BarChart
                                        data={topPosts}
                                        layout="vertical"
                                        margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                                        <XAxis type="number" hide allowDecimals={false} />
                                        <YAxis
                                            type="category"
                                            dataKey="title"
                                            width={140}
                                            tick={{ fontSize: 12, fill: "#475569" }}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(t) => (t.length > 22 ? `${t.slice(0, 22)}…` : t)}
                                        />
                                        <Tooltip
                                            formatter={(value) => [formatNumber(value), "Acessos"]}
                                            contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13 }}
                                        />
                                        <Bar dataKey="views" fill="#1a73e8" radius={[0, 6, 6, 0]} barSize={18} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </section>

                        {/* Dispositivos */}
                        <section className={styles.card}>
                            <div className={styles.cardHeader}>
                                <Monitor size={17} aria-hidden="true" />
                                <h3>Dispositivos</h3>
                            </div>
                            {devices.length === 0 ? (
                                <p className={styles.muted}>Sem dados.</p>
                            ) : (
                                <div className={styles.pieWrap}>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <PieChart>
                                            <Pie
                                                data={devices}
                                                dataKey="value"
                                                nameKey="label"
                                                innerRadius={50}
                                                outerRadius={80}
                                                paddingAngle={2}
                                            >
                                                {devices.map((entry, i) => (
                                                    <Cell key={entry.label} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value, name) => [formatNumber(value), name]}
                                                contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13 }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <ul className={styles.legend}>
                                        {devices.map((entry, i) => (
                                            <li key={entry.label}>
                                                <span
                                                    className={styles.legendDot}
                                                    style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                                                />
                                                <span className={styles.legendLabel}>{entry.label}</span>
                                                <strong>{formatNumber(entry.value)}</strong>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </section>
                    </div>

                    <div className={styles.grid3}>
                        {/* Origens */}
                        <RankList
                            icon={<Globe size={17} aria-hidden="true" />}
                            title="Origem do tráfego"
                            items={sources.map((s) => ({ label: s.label, value: s.value }))}
                            emptyText="Sem dados de origem."
                        />

                        {/* Navegadores */}
                        <RankList
                            icon={<Monitor size={17} aria-hidden="true" />}
                            title="Navegadores"
                            items={browsers.map((b) => ({ label: b.label, value: b.value }))}
                            emptyText="Sem dados de navegador."
                        />

                        {/* Localizações */}
                        <RankList
                            icon={<MapPin size={17} aria-hidden="true" />}
                            title="Localizações"
                            items={locations.map((l) => ({
                                label: [l.city, l.region].filter(Boolean).join(" — ") || l.country || "Desconhecido",
                                value: l.views,
                            }))}
                            emptyText="Sem dados de localização."
                        />
                    </div>
                </>
            )}
        </div>
    );
}

function RankList({ icon, title, items, emptyText }) {
    const max = items.reduce((m, it) => Math.max(m, Number(it.value || 0)), 0) || 1;
    return (
        <section className={styles.card}>
            <div className={styles.cardHeader}>
                {icon}
                <h3>{title}</h3>
            </div>
            {items.length === 0 ? (
                <p className={styles.muted}>{emptyText}</p>
            ) : (
                <ul className={styles.rankList}>
                    {items.map((it) => (
                        <li key={it.label}>
                            <div className={styles.rankRow}>
                                <span className={styles.rankLabel} title={it.label}>
                                    {it.label}
                                </span>
                                <strong>{formatNumber(it.value)}</strong>
                            </div>
                            <div className={styles.rankBar}>
                                <span style={{ width: `${(Number(it.value || 0) / max) * 100}%` }} />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
